/**
 * Dictionary of entities that have forms in the Flora CMS application
 * This file maps entity names to their contextPrompts for AI assistant
 * Only includes entities that have add/edit forms in the app routing
 */

export interface EntityInfo {
  name: string;
  hasHistory?: boolean;
  contextPrompt: string;
  propertiesToBeGenerated: string[];
  businessRules: string[];
  propertyDescriptions: Record<string, string>;
  sampleIds?: number[];
  promptKeys?: string[];
}

export const ENTITIES_WITH_FORMS: Record<string, EntityInfo> = {
  // Core Game Entities
  'items': {
    name: 'Items',
    hasHistory: true,
    contextPrompt: 'The Items table is used to define game items and assets that players can collect, use, or interact with',
    propertiesToBeGenerated: ['name', 'blurb', 'latinName', 'cultivar', 'plantFamily'],
    businessRules: [
      'Plant items must have botanically accurate Latin names and cultivars',
      'Names should be appealing and memorable for players',
      'Blurbs should be educational yet engaging, highlighting key visual or growth characteristics',
      'Avoid overly technical jargon in player-facing content'
    ],
    propertyDescriptions: {
      'name': 'The display name players see - should be appealing, memorable, and match the item\'s visual appearance or theme',
      'blurb': 'Short marketing description (100-120 chars) that appears in game UI - should be engaging, educational, and mention key visual features',
      'latinName': 'Scientific botanical name using proper Latin nomenclature (Genus species format)',
      'cultivar': 'Specific plant variety name - should be real cultivar names when possible, enclosed in single quotes',
      'plantFamily': 'The family of the plant - should be a real plant family name'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'collections': {
    name: 'Collections',
    contextPrompt: 'The Collections table is used to group related items and content together for organizational purposes',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Collection names should clearly indicate the grouping theme or category',
      'Names should be concise and easily understood by players',
      'Avoid overly generic names - be specific about the collection focus'
    ],
    propertyDescriptions: {
      'name': 'Clear, descriptive name that indicates what items are grouped together (e.g., "Spring Flowers", "Garden Tools", "Rare Orchids")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'challenges': {
    name: 'Challenges',
    contextPrompt: 'Challenges are comprised of a scene, which contains spawners, which you can place items in. Challenges can have a variety of properties, such as a name, description, and image.',
    propertiesToBeGenerated: ['name', 'description', 'descriptionShort'],
    businessRules: [
      'Challenge names should be motivational and clearly indicate the objective',
      'Spawners are the items that players can place in the scene',
      'Spawners can have a variety of properties, such as a name, description, and image',
      'Do not mention the spawners directly, elaborate on the scene not on what the players must do',
      'Keep the description 3-4 sentences long, and the descriptionShort 1-2 sentences long',
      'Description should be a long description of the challenge, and descriptionShort should be a short description of the challenge.',
      'Make sure not to describe the description or descriptionShort, only elaborate on the scene. Make the description detailed and the descriptionShort concise.',
      'The challenge must have a subject. Eg. this family... description, this profession... description, etc.'
    ],
    propertyDescriptions: {
      'name': 'Motivational challenge title that clearly indicates the main objective (e.g., "Master Gardener", "Rose Collection Expert")',
      'description': 'Detailed narrative description that sets the scene and atmosphere for the challenge, providing rich context and background. By default add a subject to the description. Eg. a Family, a random profession, etc. Make sure the emphasis is on designing',
      'descriptionShort': 'Brief narrative description that captures the key theme and setting of the challenge in 1-2 sentences. Eg. a Family, a random profession, etc. Make sure the emphasis is on designing. Keep it consistent with the description.'
    },
    sampleIds: [3003, 4003, 5003, 4453, 5654],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'achievements': {
    name: 'Achievements',
    contextPrompt: 'The Achievements table is used to define player achievements and rewards for completing specific tasks',
    propertiesToBeGenerated: ['title', 'subText', 'playerDescription'],
    businessRules: [
      'Achievement titles should be celebratory and rewarding',
      'Player descriptions should make players feel accomplished',
      'Use positive, encouraging language that motivates continued play',
      'Be specific about what was accomplished without being overly technical'
    ],
    propertyDescriptions: {
      'title': 'Celebratory achievement name that makes players feel accomplished (e.g., "Garden Master", "Bloom Specialist")',
      'subText': 'Brief additional context or category for the achievement (e.g., "Collection Expert", "Seasonal Champion")',
      'playerDescription': 'Encouraging description that explains what the player accomplished and why it\'s impressive - should make them feel proud'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'series': {
    name: 'Series',
    contextPrompt: 'The Series table is used to define game series and storylines that connect related content',
    propertiesToBeGenerated: ['name', 'copy'],
    businessRules: [
      'Series names should be thematic and create anticipation',
      'Copy should tell a compelling story that connects the series content',
      'Maintain narrative consistency across related content',
      'Create emotional connection to encourage player engagement'
    ],
    propertyDescriptions: {
      'name': 'Thematic series title that captures the storyline or seasonal theme (e.g., "Spring Awakening", "Heritage Garden Mystery")',
      'copy': 'Engaging narrative description that sets up the story, creates anticipation, and explains how this series connects related content'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'infinite-series': {
    name: 'Infinite Series',
    contextPrompt: 'The Infinite Series table is used to define infinite progression series that continue indefinitely',
    propertiesToBeGenerated: ['name', 'copy'],
    businessRules: [
      'Names should imply ongoing progression and endless possibilities',
      'Copy should motivate continued participation without promising an end',
      'Emphasize the journey and continuous growth rather than completion',
      'Create sense of ongoing achievement and progress'
    ],
    propertyDescriptions: {
      'name': 'Progressive series title that implies ongoing growth and endless possibilities (e.g., "Eternal Garden", "Master Cultivator Path")',
      'copy': 'Motivational description that emphasizes continuous progression, ongoing rewards, and the journey of improvement'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'releases': {
    name: 'Releases',
    contextPrompt: 'The Releases table is used to manage game version releases and their associated content',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Release names should be clear and version-appropriate',
      'Include seasonal or thematic elements when relevant',
      'Be descriptive enough for development team to understand content focus'
    ],
    propertyDescriptions: {
      'name': 'Clear release identifier that indicates version, season, or major content theme (e.g., "Spring 2024 Release", "Heritage Garden Update")'
    },
    sampleIds: []
  },
  
  'season-pass': {
    name: 'Season Pass',
    contextPrompt: 'The Season Pass table is used to define seasonal progression passes and their rewards',
    propertiesToBeGenerated: ['name', 'copy'],
    businessRules: [
      'Names should create excitement and indicate seasonal theme',
      'Copy should highlight exclusive rewards and limited-time nature',
      'Emphasize progression and achievement',
      'Create urgency while remaining appealing'
    ],
    propertyDescriptions: {
      'name': 'Exciting seasonal pass title that indicates the theme and creates desire to participate (e.g., "Summer Bloom Pass", "Autumn Harvest Journey")',
      'copy': 'Compelling description that highlights exclusive rewards, progression system, and limited-time nature to encourage purchase'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Content and Media
  'patterns': {
    name: 'Patterns',
    contextPrompt: 'The Patterns table is used to define what kind of pattern an item must have',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Pattern names should be descriptive of visual appearance',
      'Use clear, recognizable pattern terminology',
      'Consider how patterns will be used in game context'
    ],
    propertyDescriptions: {
      'name': 'Descriptive pattern name that clearly indicates the visual design (e.g., "Striped", "Polka Dot", "Floral Vine", "Geometric")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'shapes': {
    name: 'Shapes',
    contextPrompt: 'The Shapes table is used to define shape definitions and properties for items',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Shape names should be clear and universally understood',
      'Use standard geometric or descriptive terms',
      'Consider how shapes affect gameplay or visual design'
    ],
    propertyDescriptions: {
      'name': 'Clear geometric or descriptive shape name (e.g., "Round", "Heart-shaped", "Star", "Elongated", "Cascading")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'styles': {
    name: 'Styles',
    contextPrompt: 'The Styles table is used to configure visual style settings and appearances',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Style names should indicate aesthetic or design approach',
      'Use terminology that designers and players can understand',
      'Consider thematic consistency across the game'
    ],
    propertyDescriptions: {
      'name': 'Aesthetic style name that indicates design approach (e.g., "Rustic", "Modern", "Victorian", "Cottage Garden", "Minimalist")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'traits': {
    name: 'Traits',
    contextPrompt: 'The Traits table is used to define character and item traits that affect gameplay',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Trait names should clearly indicate the characteristic or ability',
      'Use positive language even for limiting traits',
      'Consider impact on gameplay balance and player understanding'
    ],
    propertyDescriptions: {
      'name': 'Clear trait name that indicates a characteristic or ability (e.g., "Fast Growing", "Drought Resistant", "Fragrant", "Rare Bloom")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'materials': {
    name: 'Materials',
    contextPrompt: 'The Materials table is used to assign a kind of material to an item',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Material names should be realistic and recognizable',
      'Consider physical properties that affect item appearance',
      'Use standard material terminology'
    ],
    propertyDescriptions: {
      'name': 'Material type name that indicates physical composition (e.g., "Ceramic", "Wood", "Metal", "Stone", "Fabric", "Plastic")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'colors': {
    name: 'Colors',
    contextPrompt: 'The Colors table is used to assign a color to items or plants',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Color names should be descriptive and appealing',
      'Use names that evoke the actual visual appearance',
      'Consider using nature-inspired or poetic color names for plants'
    ],
    propertyDescriptions: {
      'name': 'Descriptive color name that evokes the visual appearance (e.g., "Crimson Red", "Sunset Orange", "Ocean Blue", "Forest Green")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'audio-collections': {
    name: 'Audio Collections',
    contextPrompt: 'The Audio Collections table is used to manage collections of audio assets and sound files',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Names should indicate the audio theme or usage context',
      'Be descriptive enough for sound designers to understand purpose',
      'Consider where and how audio will be used in game'
    ],
    propertyDescriptions: {
      'name': 'Audio collection name that indicates theme or usage (e.g., "Garden Ambience", "UI Sound Effects", "Seasonal Music")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'loading-screens': {
    name: 'Loading Screens',
    contextPrompt: 'The Loading Screens table is used to configure loading screen images and content',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Names should indicate the visual theme or usage context',
      'Consider seasonal or event-specific loading screens',
      'Be descriptive for content management purposes'
    ],
    propertyDescriptions: {
      'name': 'Loading screen identifier that indicates visual theme or usage context (e.g., "Spring Garden", "Tutorial Intro", "Season Pass")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Game Mechanics
  'perks': {
    name: 'Perks',
    contextPrompt: 'The Perks table is used to define player perks and bonuses that enhance gameplay',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Perk names should clearly indicate the benefit provided',
      'Use positive, appealing language that makes players want the perk',
      'Be specific about the advantage gained'
    ],
    propertyDescriptions: {
      'name': 'Clear perk name that indicates the benefit or bonus provided (e.g., "Fast Growth", "Double Harvest", "Weather Protection")'
    },
    sampleIds: []
  },
  
  'currencies': {
    name: 'Currencies',
    contextPrompt: 'The Currencies table is used to define in-game currencies and their properties',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Currency names should fit the garden/nature theme',
      'Avoid real-world currency names to prevent confusion',
      'Names should indicate value or usage context'
    ],
    propertyDescriptions: {
      'name': 'Thematic currency name that fits the garden setting (e.g., "Garden Coins", "Bloom Tokens", "Seed Points", "Growth Gems")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'resources': {
    name: 'Resources',
    contextPrompt: 'The Resources table is used to define game resources and materials that players can collect',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Resource names should be intuitive and garden-related',
      'Indicate the type of resource and its potential uses',
      'Use familiar terminology that players can easily understand'
    ],
    propertyDescriptions: {
      'name': 'Garden resource name that indicates type and usage (e.g., "Fertile Soil", "Rainwater", "Compost", "Plant Food", "Mulch")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'restrictions': {
    name: 'Restrictions',
    contextPrompt: 'The Restrictions table is used to define access and usage restrictions for content',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Restriction names should clearly indicate what is limited',
      'Use neutral language that doesn\'t frustrate players',
      'Be specific about the type of restriction'
    ],
    propertyDescriptions: {
      'name': 'Clear restriction name that indicates what is limited (e.g., "Level Requirement", "Seasonal Only", "Premium Access", "Event Exclusive")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'progression-levels': {
    name: 'Progression Levels',
    contextPrompt: 'The Progression Levels table is used to define player progression and leveling systems',
    propertiesToBeGenerated: ['name', 'copy'],
    businessRules: [
      'Level names should indicate progression and achievement',
      'Copy should motivate players to reach the next level',
      'Create sense of advancement and mastery',
      'Highlight rewards and new capabilities unlocked'
    ],
    propertyDescriptions: {
      'name': 'Progressive level name that indicates advancement (e.g., "Novice Gardener", "Bloom Expert", "Master Cultivator")',
      'copy': 'Motivational description that explains what this level represents and what new capabilities or rewards it unlocks'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'rewards-track': {
    name: 'Rewards Track',
    contextPrompt: 'The Rewards Track table is used to define reward progression tracks and their benefits',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Track names should indicate the reward theme or progression type',
      'Create anticipation for the rewards contained within',
      'Be specific about the type of progression'
    ],
    propertyDescriptions: {
      'name': 'Reward track name that indicates progression theme (e.g., "Daily Gardener", "Collection Master", "Seasonal Explorer")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'daily-goal': {
    name: 'Daily Goals',
    contextPrompt: 'The Daily Goals table is used to define daily objectives and goals for players',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Goal names should be clear and achievable-sounding',
      'Use action-oriented language that motivates daily play',
      'Indicate what activity the player should focus on'
    ],
    propertyDescriptions: {
      'name': 'Clear daily objective that motivates specific gameplay (e.g., "Plant 5 Flowers", "Water Your Garden", "Harvest Rewards")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'daily-rewards': {
    name: 'Daily Rewards',
    contextPrompt: 'The Daily Rewards table is used to configure daily login rewards and bonuses',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Reward names should create anticipation and value',
      'Indicate the type or theme of reward',
      'Use appealing language that encourages daily login'
    ],
    propertyDescriptions: {
      'name': 'Appealing reward name that creates desire (e.g., "Garden Starter Pack", "Bloom Bonus", "Cultivation Kit")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Nurture System
  'nurture': {
    name: 'Nurture',
    contextPrompt: 'The Nurture table is used to define the plant nurturing system and growth mechanics',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Nurture names should relate to plant care and growth',
      'Use terminology that reflects real gardening practices',
      'Consider the emotional connection between player and plants'
    ],
    propertyDescriptions: {
      'name': 'Plant care activity name that relates to nurturing growth (e.g., "Gentle Watering", "Soil Enrichment", "Pruning Care")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'nurture-collection': {
    name: 'Nurture Collection',
    contextPrompt: 'The Nurture Collection table is used to organize collections of nurture items and plants',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Collection names should indicate the nurturing theme or plant group',
      'Create emotional connection to plant care',
      'Use terminology that appeals to gardening enthusiasts'
    ],
    propertyDescriptions: {
      'name': 'Nurture collection name that groups related plants or care activities (e.g., "Tender Seedlings", "Blooming Beauties", "Heritage Varieties")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'nurture-collection-groups': {
    name: 'Nurture Collection Groups',
    contextPrompt: 'The Nurture Collection Groups table is used to organize groups of nurture collections',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Group names should organize collections by meaningful categories',
      'Use broad themes that encompass multiple collections',
      'Consider seasonal or difficulty-based groupings'
    ],
    propertyDescriptions: {
      'name': 'High-level grouping name for nurture collections (e.g., "Seasonal Plants", "Indoor Garden", "Expert Cultivation")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'nurture-card': {
    name: 'Nurture Cards',
    contextPrompt: 'The Nurture Cards table is used to define nurture trading cards and their properties',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Card names should be collectible and appealing',
      'Create sense of rarity and value',
      'Use names that players want to show off'
    ],
    propertyDescriptions: {
      'name': 'Collectible card name that creates desire and indicates rarity (e.g., "Golden Rose Card", "Master Gardener Badge", "Rare Bloom Certificate")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'nurture-rarity': {
    name: 'Nurture Rarity',
    contextPrompt: 'The Nurture Rarity table is used to define rarity levels for nurture items and plants',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Rarity names should indicate value and scarcity',
      'Use terminology that collectors understand',
      'Create hierarchy of desirability'
    ],
    propertyDescriptions: {
      'name': 'Rarity level name that indicates scarcity and value (e.g., "Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythical")'
    },
    sampleIds: []
  },
  
  'nurture-stage': {
    name: 'Nurture Stages',
    contextPrompt: 'The Nurture Stages table is used to define growth stages in the plant nurturing system',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Stage names should reflect natural plant development',
      'Use terminology that gardeners recognize',
      'Create sense of progression and anticipation'
    ],
    propertyDescriptions: {
      'name': 'Plant growth stage name that reflects natural development (e.g., "Seed", "Sprout", "Seedling", "Growing", "Budding", "Blooming", "Mature")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'seed-pack': {
    name: 'Seed Packs',
    contextPrompt: 'The Seed Packs table is used to configure seed package configurations and contents',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Pack names should indicate contents and theme',
      'Create excitement about potential plants',
      'Use appealing packaging-style language'
    ],
    propertyDescriptions: {
      'name': 'Appealing seed pack name that indicates contents (e.g., "Spring Starter Pack", "Wildflower Mix", "Heritage Tomato Collection")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'seed-pack-box': {
    name: 'Seed Pack Boxes',
    contextPrompt: 'The Seed Pack Boxes table is used to define seed pack container boxes and their properties',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Box names should indicate capacity and purpose',
      'Consider seasonal or thematic organization',
      'Use storage terminology that makes sense to players'
    ],
    propertyDescriptions: {
      'name': 'Container box name that indicates purpose and capacity (e.g., "Seasonal Storage Box", "Premium Seed Vault", "Starter Collection Case")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'seed-pack-box-types': {
    name: 'Seed Pack Box Types',
    contextPrompt: 'The Seed Pack Box Types table is used to define different types of seed pack boxes',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Type names should differentiate box categories clearly',
      'Indicate size, purpose, or premium level',
      'Use terminology that helps with organization'
    ],
    propertyDescriptions: {
      'name': 'Box type name that categorizes containers (e.g., "Standard Box", "Premium Vault", "Collector Edition", "Starter Container")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Store and Commerce
  'store-listings-v2': {
    name: 'Store Listings V2',
    contextPrompt: 'The Store Listings V2 table is used to manage store product listings and their availability',
    propertiesToBeGenerated: ['name', 'description'],
    businessRules: [
      'Product names should be appealing and clear about what\'s included',
      'Descriptions should highlight value and benefits',
      'Use compelling sales language while being honest about contents',
      'Create urgency or desire to purchase'
    ],
    propertyDescriptions: {
      'name': 'Appealing product name that clearly indicates what the customer receives (e.g., "Garden Deluxe Pack", "Premium Plant Bundle")',
      'description': 'Compelling sales description that highlights value, benefits, and what\'s included to encourage purchase'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'price-points': {
    name: 'Price Points',
    contextPrompt: 'The Price Points table is used to configure pricing strategies and cost structures',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Price point names should indicate value tier or customer segment',
      'Use clear terminology for internal pricing strategy',
      'Consider psychological pricing principles'
    ],
    propertyDescriptions: {
      'name': 'Price tier name that indicates value level (e.g., "Budget Friendly", "Standard Value", "Premium Tier", "Collector Price")'
    },
    sampleIds: []
  },
  
  'endless-offer': {
    name: 'Endless Offers',
    contextPrompt: 'The Endless Offers table is used to define recurring promotional offers and deals',
    propertiesToBeGenerated: ['name', 'copy'],
    businessRules: [
      'Offer names should create urgency and appeal',
      'Copy should highlight savings and exclusive nature',
      'Emphasize limited-time aspect even for recurring offers',
      'Use persuasive marketing language'
    ],
    propertyDescriptions: {
      'name': 'Compelling offer name that creates urgency and desire (e.g., "Weekly Garden Boost", "Flash Sale Special", "Member Exclusive Deal")',
      'copy': 'Persuasive marketing copy that highlights savings, exclusive benefits, and creates urgency to act now'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'seasonal-prizes': {
    name: 'Seasonal Prizes',
    contextPrompt: 'The Seasonal Prizes table is used to configure seasonal event prizes and rewards',
    propertiesToBeGenerated: ['name', 'copy'],
    businessRules: [
      'Prize names should be seasonal and desirable',
      'Copy should create excitement about limited-time availability',
      'Emphasize exclusivity and seasonal theme',
      'Motivate participation in seasonal events'
    ],
    propertyDescriptions: {
      'name': 'Exciting seasonal prize name that matches the event theme (e.g., "Spring Crown Jewel", "Autumn Harvest Trophy", "Winter Wonder Gift")',
      'copy': 'Exciting description that emphasizes seasonal exclusivity and motivates participation in events to earn the prize'
    },
    sampleIds: []
  },
  
  'vendors': {
    name: 'Vendors',
    contextPrompt: 'The Vendors table is used to define in-game vendors and merchants with their inventories',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Vendor names should fit the garden theme and world setting',
      'Create memorable characters that players want to visit',
      'Consider the vendor\'s specialty or personality'
    ],
    propertyDescriptions: {
      'name': 'Memorable vendor name that fits the garden world and indicates their specialty (e.g., "Rosa\'s Rose Shop", "Sam\'s Seeds", "Garden Guru Glen")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Organization and Categories
  'categories': {
    name: 'Categories',
    contextPrompt: 'The Categories table is used for content categorization and organizational structure',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Category names should be clear and intuitive for organization',
      'Use standard terminology that users expect',
      'Consider how categories will be used in navigation and search'
    ],
    propertyDescriptions: {
      'name': 'Clear organizational category name (e.g., "Flowers", "Vegetables", "Tools", "Decorations", "Seasonal Items")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'category-collections': {
    name: 'Category Collections',
    contextPrompt: 'The Category Collections table is used to manage collections within specific categories',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Collection names should indicate both category and specific grouping',
      'Be more specific than the parent category',
      'Help users find related items quickly'
    ],
    propertyDescriptions: {
      'name': 'Specific collection name within a category (e.g., "Spring Bulbs", "Heirloom Roses", "Garden Hand Tools")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'item-sets': {
    name: 'Item Sets',
    contextPrompt: 'The Item Sets table is used to define predefined collections of related items',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Set names should indicate the theme or purpose of grouped items',
      'Create appeal for obtaining the complete set',
      'Use terminology that suggests completeness and coordination'
    ],
    propertyDescriptions: {
      'name': 'Cohesive item set name that suggests related items work together (e.g., "English Garden Set", "Modern Patio Collection", "Vintage Tool Kit")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'item-types': {
    name: 'Item Types',
    contextPrompt: 'The Item Types table is used for item classification and type definitions',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Type names should clearly categorize items by function or nature',
      'Use standard terminology for classification',
      'Consider how types affect gameplay mechanics'
    ],
    propertyDescriptions: {
      'name': 'Item classification name that categorizes by function (e.g., "Plant", "Tool", "Decoration", "Fertilizer", "Container")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Challenge System
  'challenge-collections': {
    name: 'Challenge Collections',
    contextPrompt: 'The Challenge Collections table is used to organize collections of related challenges',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Collection names should group challenges by theme or difficulty',
      'Create progression sense from easier to harder challenges',
      'Use motivational language that encourages completion'
    ],
    propertyDescriptions: {
      'name': 'Challenge group name that organizes related objectives (e.g., "Beginner Gardener", "Master Challenges", "Seasonal Quests")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'challenge-types': {
    name: 'Challenge Types',
    contextPrompt: 'The Challenge Types table is used to define types and categories of challenges',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Type names should categorize challenges by mechanics or goals',
      'Help players understand what kind of activity is required',
      'Use clear, gameplay-focused terminology'
    ],
    propertyDescriptions: {
      'name': 'Challenge category name that indicates the type of objective (e.g., "Collection", "Daily Task", "Achievement", "Event", "Social")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'challenges-keywords': {
    name: 'Challenge Keywords',
    contextPrompt: 'The Challenge Keywords table is used for keywords that help categorize challenges',
    propertiesToBeGenerated: ['name', 'description'],
    businessRules: [
      'Keywords should be searchable and descriptive',
      'Help players find challenges that match their interests',
      'Use terminology that players naturally think of'
    ],
    propertyDescriptions: {
      'name': 'Searchable keyword for challenge categorization (e.g., "flowers", "watering", "harvesting", "seasonal", "beginner")',
      'description': 'Brief explanation of what this keyword represents and what types of challenges it applies to'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'sourcing-groups': {
    name: 'Sourcing Groups',
    contextPrompt: 'The Sourcing Groups table is used to manage content sourcing groups and their configurations',
    propertiesToBeGenerated: ['name', 'blurb'],
    businessRules: [
      'Group names should indicate the source or acquisition method',
      'Blurbs should explain how players can obtain items from this group',
      'Be clear about requirements or special conditions'
    ],
    propertyDescriptions: {
      'name': 'Content sourcing group name that indicates how items are obtained (e.g., "Shop Purchase", "Event Rewards", "Achievement Unlocks")',
      'blurb': 'Brief explanation of how players can obtain items from this sourcing group and any special requirements'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'sourcing-challenge-groups': {
    name: 'Sourcing Challenge Groups',
    contextPrompt: 'The Sourcing Challenge Groups table is used to define challenge-specific sourcing groups',
    propertiesToBeGenerated: ['name', 'description'],
    businessRules: [
      'Names should indicate challenge-specific acquisition methods',
      'Descriptions should explain the challenge connection',
      'Be specific about what challenges provide access'
    ],
    propertyDescriptions: {
      'name': 'Challenge-specific sourcing group name (e.g., "Weekly Challenge Rewards", "Tournament Prizes", "Daily Quest Unlocks")',
      'description': 'Detailed explanation of which challenges provide access to this sourcing group and how the system works'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Achievements System
  'achievements-collection': {
    name: 'Achievement Collections',
    contextPrompt: 'The Achievement Collections table is used to organize collections of related achievements',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Collection names should group achievements by theme or progression',
      'Create sense of mastery and accomplishment',
      'Use terminology that makes players proud to complete'
    ],
    propertyDescriptions: {
      'name': 'Achievement collection name that groups related accomplishments (e.g., "Garden Master Series", "Seasonal Champion", "Collection Expert")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'titles': {
    name: 'Titles',
    contextPrompt: 'The Titles table is used to define player titles and ranks that can be earned',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Titles should make players feel accomplished and special',
      'Use prestigious language that players want to display',
      'Create hierarchy of prestige and achievement'
    ],
    propertyDescriptions: {
      'name': 'Prestigious player title that indicates mastery or achievement (e.g., "Garden Sage", "Bloom Whisperer", "Master Cultivator", "Plant Guru")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  // Game Features
  'minigames': {
    name: 'Mini Games',
    contextPrompt: 'The Mini Games table is used to configure mini-game settings and mechanics',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Game names should be fun and indicate the activity',
      'Create excitement and encourage participation',
      'Use terminology that suggests entertainment value'
    ],
    propertyDescriptions: {
      'name': 'Fun mini-game name that indicates the activity (e.g., "Seed Sorting", "Garden Puzzle", "Bloom Match", "Plant Care Rush")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'co-op': {
    name: 'Co-op',
    contextPrompt: 'The Co-op table is used to define cooperative gameplay features and multiplayer mechanics',
    propertiesToBeGenerated: ['name', 'copy', 'currency_name', 'currency_name_plural', 'goal_name', 'goal_name_plural', 'invite_text', 'welcome_text'],
    businessRules: [
      'All text should encourage cooperation and team spirit',
      'Currency and goal names should fit the collaborative theme',
      'Invite and welcome text should be friendly and motivating',
      'Create sense of shared achievement and community'
    ],
    propertyDescriptions: {
      'name': 'Cooperative activity name that emphasizes teamwork (e.g., "Community Garden Project", "Neighborhood Bloom Challenge")',
      'copy': 'Engaging description that explains the cooperative activity and motivates team participation',
      'currency_name': 'Singular name for currency used in this co-op activity (e.g., "Team Point", "Unity Token")',
      'currency_name_plural': 'Plural form of the currency name (e.g., "Team Points", "Unity Tokens")',
      'goal_name': 'Singular name for goals in this co-op activity (e.g., "Shared Objective", "Team Mission")',
      'goal_name_plural': 'Plural form of the goal name (e.g., "Shared Objectives", "Team Missions")',
      'invite_text': 'Friendly invitation message to encourage friends to join the co-op activity',
      'welcome_text': 'Welcoming message for new participants explaining the cooperative experience'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'climates': {
    name: 'Climates',
    contextPrompt: 'The Climates table is used to define environmental climate settings that affect plant growth',
    propertiesToBeGenerated: ['name'],
    businessRules: [
      'Climate names should be realistic and recognizable',
      'Consider how different climates affect plant growth in game',
      'Use geographical or descriptive terminology'
    ],
    propertyDescriptions: {
      'name': 'Realistic climate name that affects plant growth (e.g., "Tropical", "Mediterranean", "Temperate", "Arctic", "Desert", "Rainforest")'
    },
    sampleIds: [],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'inbox-messages': {
    name: 'Inbox Messages',
    contextPrompt: 'The inbox-messages table is used to save all the notifications shown to users to notify challenges, rewards, friendship invitation, new items, etc',
    propertiesToBeGenerated: ['headline', 'title', 'body'],
    businessRules: [
      'All message content should be clear and actionable',
      'Headlines should grab attention without being clickbait',
      'Body text should provide necessary information concisely',
      'Maintain friendly, helpful tone throughout',
      'if the inbox is related to bundles and, it should make sure that it states that the bundle is now available at the Premium Store',
      'Inboxes should always be signed by The Garden Joy Team',
      'Avoid listing the items in the body of the inbox, just mention the items generally',
      'The body should always contain a {image} tag to represent where the image of the inbox is located'
    ],
    propertyDescriptions: {
      'headline': 'Attention-grabbing headline that summarizes the message purpose (short and impactful)',
      'title': 'Clear message title that explains what the notification is about',
      'body': 'Detailed message content that provides all necessary information and clear next steps for the player. This should have a {image} tag to represent where the image of the inbox is located. the image tag should always be inside the same property, not as a separate property'
    },
    sampleIds: [2262, 2252, 2253, 2254, 2255, 2291, 2281, 1874, 2248, 2230],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  },
  
  'external-plant-data': {
    name: 'External Plant Data',
    contextPrompt: 'The External Plant Data table is used to store comprehensive botanical information imported from external sources, containing detailed plant characteristics, growth requirements, and environmental data',
    propertiesToBeGenerated: ['name', 'description', 'commonName', 'botanicalName', 'plantType', 'matureSize', 'growingTime', 'bloomTime', 'flowerColor', 'soilType', 'soilPH', 'hardinessZone', 'nativeArea', 'sunExposure', 'toxicity'],
    businessRules: [
      'All botanical data should be scientifically accurate and verified',
      'Botanical names must follow proper Latin nomenclature standards',
      'Common names should include widely recognized regional variations',
      'Growth characteristics should be realistic and based on actual plant behavior',
      'Environmental requirements should reflect real-world growing conditions',
      'Toxicity information must be accurate for safety purposes',
      'Data should be comprehensive enough for educational and gameplay purposes'
    ],
    propertyDescriptions: {
      'name': 'Primary plant name used for identification and display purposes',
      'description': 'Comprehensive description of the plant including appearance, characteristics, and notable features',
      'commonName': 'Array of common names used for this plant in different regions and languages',
      'botanicalName': 'Array of scientific botanical names following proper Latin nomenclature (Genus species format)',
      'plantType': 'Array of plant type classifications (e.g., "Perennial", "Annual", "Shrub", "Tree", "Herb")',
      'matureSize': 'Expected mature size of the plant (height and spread) in appropriate units',
      'growingTime': 'Time required for the plant to reach maturity or bloom from seed/planting',
      'bloomTime': 'Seasonal timing when the plant typically flowers or produces blooms',
      'flowerColor': 'Primary color(s) of the plant\'s flowers or blooms',
      'soilType': 'Preferred soil type for optimal growth (e.g., "Well-draining", "Sandy", "Loamy", "Clay")',
      'soilPH': 'Optimal soil pH range for this plant (e.g., "6.0-7.0", "Acidic", "Neutral")',
      'hardinessZone': 'USDA hardiness zones where this plant can survive winter temperatures',
      'nativeArea': 'Array of geographic regions where this plant is naturally found',
      'sunExposure': 'Sunlight requirements (e.g., "Full Sun", "Partial Shade", "Full Shade")',
      'toxicity': 'Information about plant toxicity to humans or animals, if any'
    },
    sampleIds: [1000, 1001, 1002, 1003, 1004],
    promptKeys: ['MAX_CHARACTER_COUNT', 'OPTIONS_COUNT', 'OUTPUT_JSON', 'BANNED_WORDS']
  }
};

/**
 * Get entity information by name
 */
export function getEntityByName(name: string): EntityInfo | undefined {
  return ENTITIES_WITH_FORMS[name];
}

/**
 * Get all entity names
 */
export function getAllEntityNames(): string[] {
  return Object.keys(ENTITIES_WITH_FORMS);
}

/**
 * Get all entity context prompts
 */
export function getAllEntityContextPrompts(): string[] {
  return Object.values(ENTITIES_WITH_FORMS).map(entity => entity.contextPrompt);
}

/**
 * Filter entities by category based on contextPrompt or name
 */
export function getEntitiesByCategory(category: string): EntityInfo[] {
  const categoryLower = category.toLowerCase();
  return Object.values(ENTITIES_WITH_FORMS).filter(entity => 
    entity.name.toLowerCase().includes(categoryLower) || 
    entity.contextPrompt.toLowerCase().includes(categoryLower)
  );
}

/**
 * Get enhanced context for an entity including business rules and property descriptions
 * This demonstrates what the AI assistant will receive as context
 */
export function getEnhancedContextForEntity(entityKey: string): string | null {
  const entityInfo = getEntityByName(entityKey);
  
  if (!entityInfo) {
    return null;
  }

  // Return just the base context prompt - business rules and property descriptions 
  // are now sent as separate structured data in the AI assistant service
  return entityInfo.contextPrompt;
}

/**
 * Get all entities that have specific properties to be generated
 */
export function getEntitiesWithProperty(propertyName: string): EntityInfo[] {
  return Object.values(ENTITIES_WITH_FORMS).filter(entity => 
    entity.propertiesToBeGenerated.includes(propertyName)
  );
}

/**
 * Filter sample data to only include properties that should be generated
 * This demonstrates how sample data will be filtered for the AI assistant
 */
export function filterSampleDataForEntity(entityKey: string, sampleData: any[]): any[] {
  const entityInfo = getEntityByName(entityKey);
  
  if (!entityInfo || !sampleData || sampleData.length === 0) {
    return sampleData || [];
  }

  return sampleData.map((item: any) => {
    const filtered: any = {};
    entityInfo.propertiesToBeGenerated.forEach(prop => {
      if (item[prop] !== undefined) {
        filtered[prop] = item[prop];
      }
    });
    return filtered;
  }).filter((item: any) => Object.keys(item).length > 0);
}

/**
 * Get example of what the AI assistant will receive for a specific entity
 * This is useful for testing and understanding the context structure
 */
export function getExampleContextForEntity(entityKey: string, sampleData: any[]): {
  context: string;
  filteredSample: any[];
  businessRules: string[];
  propertyDescriptions: Record<string, string>;
  fullStructure: object;
} | null {
  const entityInfo = getEntityByName(entityKey);
  
  if (!entityInfo || !sampleData || sampleData.length === 0) {
    return null;
  }

  const context = getEnhancedContextForEntity(entityKey) || '';
  const filteredSample = filterSampleDataForEntity(entityKey, sampleData);
  const fullContext = `${context}. Take the follow data as sample to generate a new record: ${JSON.stringify(filteredSample)}`;

  const fullStructure = {
    isContext: true,
    type: 'user_message',
    subType: 'form',
    entity: entityKey,
    context: fullContext,
    businessRules: entityInfo.businessRules,
    propertyDescriptions: entityInfo.propertyDescriptions,
    filteredSample: filteredSample
  };

  return {
    context: fullContext,
    filteredSample,
    businessRules: entityInfo.businessRules,
    propertyDescriptions: entityInfo.propertyDescriptions,
    fullStructure
  };
} 